import React, { useEffect, useState, useRef } from "react";
import "./InitialAssessmentReport.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const InitialAssessmentReportManual = () => {
  const [data, setData] = useState(null);
  const [formData, setFormData] = useState({});
  const { claimNumber } = useParams();
  const [isDownloading, setIsDownloading] = useState(false);
  const hasDownloaded = useRef(false);
  const downloadAttempted = useRef(false);
  const navigate = useNavigate();

  // Format date to DD-MM-YYYY HH:MM:SS (same as first code)
  const formatDateTime = (dateString) => {
    if (!dateString) return "--";

    try {
      const date = new Date(dateString);
      if (isNaN(date)) return "--";

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");

      return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "--";
    }
  };

  // Utility function to capitalize first letter of each word
  const capitalizeWords = (str) => {
    if (!str || str === "--") return str;

    return str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const generatePDF = () => {
    if (isDownloading) return;

    setIsDownloading(true);
    const input = document.querySelector(".report-container");

    if (!input) {
      console.error("Report container not found!");
      setIsDownloading(false);
      return;
    }

    html2canvas(input, {
      scale: window.innerWidth < 768 ? 1.5 : 1.3,
      useCORS: true,
      logging: false,
      windowWidth: input.scrollWidth,
      windowHeight: input.scrollHeight,
      quality: 2.0, // Image quality 30%
    })
      .then((canvas) => {
        const imgData = canvas.toDataURL("image/jpeg", 0.5);
        const pdf = new jsPDF("p", "mm", "a4");

        const imgWidth = 210;
        const pageHeight = 297;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        hasDownloaded.current = true;

        pdf.save(`WorkApproval_${claimNumber}.pdf`);
        setIsDownloading(false);
        console.log("PDF downloaded successfully");
      })
      .catch((error) => {
        console.error("PDF generation error:", error);
        setIsDownloading(false);
      });
  };

  useEffect(() => {
    if (data) {
      downloadAttempted.current = true;
      console.log("Data loaded, triggering auto-download...");

      const timer = setTimeout(() => {
        generatePDF();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [data]);

  useEffect(() => {
    const handlePopState = () => {
      navigate(`/claimlanding/${claimNumber}`, { replace: true });
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [navigate, claimNumber]);

  // const handleApproveClick = () => {
  //   navigate(`/approve-claim/${claimNumber}`)
  // }

  // const handleManualqcClick = () => {
  //   navigate(`/manual-qc/${claimNumber}`)
  // }

  // const handleManualDownload = () => {
  //   generatePDF()
  // }

  const [claimData, setClaimData] = useState(null);

  useEffect(() => {
    const fetchClaimDetails = async () => {
      try {
        const res = await axios.post("/process_data/claim-data", {
          claimNumber,
        });
        console.log("391", res.data);
        setClaimData(res.data);
      } catch (err) {
        console.error("Error fetching claim details:", err);
      }
    };

    if (claimNumber) {
      fetchClaimDetails();
    }
  }, [claimNumber]);

  useEffect(() => {
    if (claimNumber) {
      axios
        .post(`/server/get-claim-data/${claimNumber}`)
        .then((response) => {
          console.log("24", response.data);
          setData(response.data);
          setFormData(response.data);
        })
        .catch((error) => console.error("Axios error:", error));
    }
  }, [claimNumber]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const claimDataResponse = await axios.post(
          `/server/get-claim-data/${claimNumber}`,
        );
        console.log("Claim Data:", claimDataResponse.data);
        setData(claimDataResponse.data);

        const claimDetailsResponse = await axios.post(
          "/process_data/claim-details",
          {
            claimNumber,
          },
        );
        console.log("Claim Details:", claimDetailsResponse.data);

        setFormData({
          ...claimDataResponse.data,
          ...claimDetailsResponse.data,
        });
      } catch (error) {
        console.error("Axios error:", error);
      }
    };

    if (claimNumber) {
      fetchData();
    }
  }, [claimNumber]);

  const calculateRow = (row) => {
    const qty = Number(row.qty);
    const rate = Number(row.rate);
    const taxPercent = Number(row.taxPercent);
    const depPercent = Number(row.depPercent);

    const taxable = qty * rate;
    const taxAmount = (taxable * taxPercent) / 100;
    const total = taxable + taxAmount;
    const depAmount = (taxable * depPercent) / 100;

    return { taxable, taxAmount, total, depAmount };
  };

  const subtotal = data?.parts?.reduce(
    (acc, row) => {
      const { taxable, taxAmount, total, depAmount } = calculateRow(row);
      acc.taxable += taxable;
      acc.taxAmount += taxAmount;
      acc.total += total;
      acc.depAmount += depAmount;
      return acc;
    },
    { taxable: 0, taxAmount: 0, total: 0, depAmount: 0 },
  ) || { taxable: 0, taxAmount: 0, total: 0, depAmount: 0 };
  const labourTypes = [
    { label: "Painting Charges", key: "paintingCharges" },
    { label: "Repair Charges", key: "repairCharges" },
    { label: "R&R Charges", key: "rrCharges" },
  ];
  const getSubtotal = (key) => {
    return (
      data?.parts?.reduce((sum, p) => {
        return sum + (Number(p[key]) || 0);
      }, 0) || 0
    );
  };
  const getLabourFromAPI = (label) => {
    return data?.labourDetails?.find((l) => l.labourType === label);
  };
  const getDepAmount = (key) => {
    return (
      data?.parts?.reduce((sum, p) => {
        const value = Number(p[key]) || 0;
        return sum + (value * Number(p.depPercent || 0)) / 100;
      }, 0) || 0
    );
  };
  // const partsTotal =
  //   data?.parts?.reduce((sum, p) => {
  //     const base = Number(p.qty) * Number(p.rate);
  //     const tax = (base * Number(p.taxPercent || 0)) / 100;
  //     return sum + base + tax;
  //   }, 0) || 0;

  // const labourKeys = ["paintingCharges", "repairCharges", "rrCharges"];

  // const labourTotal = labourKeys.reduce((sum, key) => {
  //   const taxable =
  //     data?.parts?.reduce((s, p) => s + (Number(p[key]) || 0), 0) || 0;

  //   const tax = (taxable * 18) / 100;

  //   return sum + taxable + tax;
  // }, 0);
  // const totalLiability = partsTotal + labourTotal;
  const labourKeys = ["paintingCharges", "repairCharges", "rrCharges"];

  const labourTotal = labourKeys.reduce((sum, key) => {
    const taxable =
      data?.parts?.reduce((s, p) => s + (Number(p[key]) || 0), 0) || 0;

    const tax = (taxable * 18) / 100;

    // ✅ fetch dep from API (same as table)
    const apiLabour = data?.labourDetails?.find((l) =>
      l.labourType
        ?.toLowerCase()
        .includes(key.replace("Charges", "").toLowerCase()),
    );

    const dep =
      apiLabour?.depAmount && apiLabour.depAmount !== "Not Found"
        ? Number(apiLabour.depAmount)
        : 0;

    // ✅ updated total
    const total = taxable + tax - dep;

    return sum + total;
  }, 0);
  const partsTotal =
    data?.parts?.reduce((sum, p) => {
      const base = Number(p.qty) * Number(p.rate);
      const tax = (base * Number(p.taxPercent || 0)) / 100;
      return sum + base + tax;
    }, 0) || 0;
  const totalLiability = partsTotal + labourTotal;
  const compulsoryDeductible =
    data?.finalCalculation?.compulsoryDeductible &&
    data.finalCalculation.compulsoryDeductible !== "Not Found"
      ? Number(data.finalCalculation.compulsoryDeductible)
      : 0;

  const insurerLiability = totalLiability - compulsoryDeductible;
  if (!data) return <div>Loading...</div>;

  return (
    <div className="report-container">
      <div className="report-header">
        <img
          src="/custom-icons/surveyorIcon.png"
          alt="Surveyor AI Logo"
          className="logo"
        />

        <div className="report-title">
          <h2>Work Approval Report</h2>
        </div>

        <p className="printed-date">Printed: {formatDateTime(new Date())}</p>
      </div>

      <div className="inline-box-row">
        <div className="inline-box label">Policy Number</div>
        <div className="inline-box value">
          {claimData?.policy_number || data?.policyNumber || ""}
        </div>
        <div className="inline-box label">Claim Number</div>
        <div className="inline-box value">{data.claimNumber}</div>
        <div className="inline-box label">Ticket Number</div>
        <div className="inline-box value">--</div>
      </div>

      <div className="inline-box-row info-box">
        <div className="info-full-row">
          <div className="inline-box label">
            Date:{formatDateTime(new Date())}
          </div>
          <div className="inline-box value">{claimData?.date || "--"}</div>
        </div>

        <div className="info-row">
          <div className="inline-box label">Name of Owner:</div>
          <div className="inline-box value">
            {data?.insured || claimData?.customer_name || "--"}
          </div>

          <div className="inline-box label">Engine No.:</div>
          <div className="inline-box value">
            {data?.EngineNumber || claimData?.engine_number || "--"}
          </div>

          <div className="inline-box label">Workshop Name:</div>
          <div className="inline-box value">
            {claimData?.workshop_name || "--"}
          </div>
        </div>

        <div className="info-row">
          <div className="inline-box label">Registration No.:</div>
          <div className="inline-box value">
            {claimData?.vehicle_number || "--"}
          </div>

          <div className="inline-box label">Surveyor Name:</div>
          <div className="inline-box value">Surveyor's Ai</div>

          <div className="inline-box label">Workshop GST Number:</div>
          <div className="inline-box value">
            {claimData?.workshop_gst || "--"}
          </div>
        </div>

        <div className="info-row">
          <div className="inline-box label">Date of Reg.:</div>
          <div className="inline-box value">
            {formatDateTime(data?.DateofRegistration)}
          </div>

          <div className="inline-box label">Claim Coordinator:</div>
          <div className="inline-box value">
            {claimData?.claimCoordinator || "--"}
          </div>

          <div className="inline-box label">Date of Accident:</div>
          <div className="inline-box value">
            {formatDateTime(claimData?.accident_datetime)}
          </div>
        </div>

        <div className="info-row">
          <div className="inline-box label">Make & Model:</div>
          <div className="inline-box value">
            {claimData?.make || ""} {claimData?.model || "--"}{" "}
            {claimData?.variant || "--"}
          </div>

          <div className="inline-box label">Chassis No.:</div>
          <div className="inline-box value">
            {data?.ChassisNumber || claimData?.chassis_number || "--"}
          </div>

          <div className="inline-box label">Vehicle Age (Years):</div>
          <div className="inline-box value">
            {(() => {
              const regDateRaw = data?.DateofRegistration;
              const accidentRaw = claimData?.accident_datetime;

              if (regDateRaw && accidentRaw) {
                const regDate = new Date(regDateRaw);
                const accidentDate = new Date(accidentRaw);

                if (!isNaN(regDate) && !isNaN(accidentDate)) {
                  let years =
                    accidentDate.getFullYear() - regDate.getFullYear();
                  let months = accidentDate.getMonth() - regDate.getMonth();
                  let days = accidentDate.getDate() - regDate.getDate();

                  if (days < 0) {
                    months -= 1;
                    days += new Date(
                      accidentDate.getFullYear(),
                      accidentDate.getMonth(),
                      0,
                    ).getDate();
                  }

                  if (months < 0) {
                    years -= 1;
                    months += 12;
                  }

                  years = Math.max(years, 1);

                  return `${years} Years ${months} Months`;
                }
              }

              return "--";
            })()}
          </div>
        </div>

        <div className="info-row">
          <div className="inline-box label">Payment to:</div>
          <div className="inline-box value">Workshop</div>
        </div>
      </div>

      <div className="mobile-only">
        <div className="report-box">
          <div className="row">
            <span className="label">Policy Number</span>
            <span className="value">{data.policyNumber}</span>
          </div>
          <div className="row">
            <span className="label">Claim Number</span>
            <span className="value">{data.claimNumber}</span>
          </div>
          <div className="row">
            <span className="label">Ticket Number</span>
            <span className="value">{data.ticketnumber}</span>
          </div>
        </div>
      </div>

      <div className="mobile-only">
        <div className="report-box">
          <div className="row">
            <span className="label">Date</span>
            <span className="value">{formatDateTime(new Date())}</span>
          </div>
          <div className="row">
            <span className="label">Assessment Date</span>
            <span className="value">{data.date}</span>
          </div>
          <div className="row">
            <span className="label">Name of Owner</span>
            <span className="value">{data.insured}</span>
          </div>
          <div className="row">
            <span className="label">Engine No.</span>
            <span className="value">{data.EngineNumber}</span>
          </div>
          <div className="row">
            <span className="label">Workshop Name</span>
            <span className="value">{data.hpa}</span>
          </div>
          <div className="row">
            <span className="label">Registration No.</span>
            <span className="value">{data.VehicleRegistrationNumber}</span>
          </div>
          <div className="row">
            <span className="label">Surveyor Name</span>
            <span className="value">Surveyor's Ai</span>
          </div>
          <div className="row">
            <span className="label">Workshop GST Number</span>
            <span className="value">--</span>
          </div>
          <div className="row">
            <span className="label">Date of Reg.</span>
            <span className="value">
              {formatDateTime(data.DateofRegistration)}
            </span>
          </div>
          <div className="row">
            <span className="label">Claim Coordinator</span>
            <span className="value">--</span>
          </div>
          <div className="row">
            <span className="label">Date of Accident</span>
            <span className="value">{formatDateTime(data.dateOfAccident)}</span>
          </div>
          <div className="row">
            <span className="label">Make & Model</span>
            <span className="value">
              {data.Make} {data.Variant}
            </span>
          </div>
          <div className="row">
            <span className="label">Chassis No.</span>
            <span className="value">{data.ChassisNumber}</span>
          </div>
          <div className="row">
            <span className="label">Vehicle Age (Years)</span>
            <span className="value">--</span>
          </div>
          <div className="row">
            <span className="label">Payment to</span>
            <span className="value">Workshop</span>
          </div>
        </div>
      </div>

      <div className="note-section">
        <p>
          "Please find the below tentative estimation for the subject vehicle as
          per the visible damages found on the vehicle. Any further
          replacement/repair of part required during the course of repair shall
          be verified and included in the estimation. We request you to take
          prior approval from the insurer for all kept open part approval, if
          any. Any further replacement/repair of part required during the course
          of repair shall be verified and included in the assessment subject to
          the admissibility of the claim. We request you to take prior approval
          from the insurer for all parts put on hold and are pending for
          approval."
        </p>
        <p>
          <strong>Important Notes:</strong>
        </p>
        <ul>
          <li>
            After completion of repair, you shall submit the vehicle for
            reinspection and also produce the salvage of parts replaced.
          </li>
          <li>
            Please have the final tax invoice raised in favor of Surveyor
            Insurance Limited up to the Insurer liability, and the remaining
            balance to be invoiced in favor of the Insured.
          </li>
        </ul>
        <p className="help-note">
          For any further clarifications regarding your claim: Contact TPA
          at&nbsp;
          {/* {formData.email_id || ' support@tpa.com'} or{' '}
          {formData.phone_number || ' +91-XXXXX-XXXXX'} */}
          {"surveyor@surveyor.com"} or {" +91-9876543210"}
        </p>
      </div>

      <h4 className="section-heading">PARTS</h4>

      <table className="parts-table">
        <thead>
          <tr>
            <th>SN</th>
            <th>JOB</th>
            <th>MATERIAL</th>
            <th>DESCRIPTION</th>
            <th>HSN</th>
            <th>QTY</th>
            <th>PART RATE</th>
            <th>TAXABLE AMOUNT</th>
            <th>TAX (%)</th>
            <th>TAX AMOUNT</th>
            <th>TOTAL AMOUNT</th>
            <th>DEP (%)</th>
            <th>DEP AMOUNT</th>
            <th>R&R CHARGES</th>
            <th>REPAIR CHARGES</th>
            <th>PAINTING CHARGES</th>
          </tr>
        </thead>
        <tbody>
          {data.parts &&
            data.parts.map((part, i) => {
              const qty = Number(part.qty) || 0;
              const rate = Number(part.rate) || 0;
              const taxPercent = Number(part.taxPercent) || 0;
              const depPercent = Number(part.depPercent) || 0;

              const taxableAmount = qty * rate;
              const taxAmount = (taxableAmount * taxPercent) / 100;
              const depAmount = (taxableAmount * depPercent) / 100;
              const totalAmount = taxableAmount + taxAmount;

              return (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{capitalizeWords(part.decision)}</td>
                  <td>{capitalizeWords(part.material)}</td>
                  <td>{capitalizeWords(part.description)}</td>
                  <td>{part.hsn}</td>
                  <td>{qty}</td>
                  <td>₹{rate}</td>
                  <td>₹{taxableAmount.toFixed(2)}</td>
                  <td>{taxPercent}</td>
                  <td>₹{taxAmount.toFixed(2)}</td>
                  <td>₹{totalAmount.toFixed(2)}</td>
                  <td>{depPercent}</td>
                  <td>₹{depAmount.toFixed(2)}</td>
                  <td>₹{part.rrCharges ?? 0}</td>
                  <td>₹{part.repairCharges ?? 0}</td>
                  <td>₹{part.paintingCharges ?? 0}</td>
                </tr>
              );
            })}

          {data?.parts && (
            <tr className="subtotal-row">
              <td colSpan="6">Subtotal</td>
              <td>
                ₹
                {data?.parts
                  ?.reduce((sum, p) => sum + Number(p.qty) * Number(p.rate), 0)
                  .toFixed(2)}
              </td>
              <td></td>
              <td></td>
              <td>
                ₹
                {data?.parts
                  ?.reduce((sum, p) => {
                    const base = Number(p.qty) * Number(p.rate);
                    return sum + (base * Number(p.taxPercent)) / 100;
                  }, 0)
                  .toFixed(2)}
              </td>
              <td>
                ₹
                {data?.parts
                  ?.reduce((sum, p) => {
                    const base = Number(p.qty) * Number(p.rate);
                    const tax = (base * Number(p.taxPercent)) / 100;
                    return sum + base + tax;
                  }, 0)
                  .toFixed(2)}
              </td>
              <td></td>
              <td>
                ₹
                {data?.parts
                  ?.reduce((sum, p) => {
                    const base = Number(p.qty) * Number(p.rate);
                    return sum + (base * Number(p.depPercent)) / 100;
                  }, 0)
                  .toFixed(2)}
              </td>
              <td>
                ₹
                {data?.parts
                  ?.reduce((sum, p) => {
                    const rrCharges = Number(p.rrCharges) || 0;
                    return sum + rrCharges;
                  }, 0)
                  .toFixed(2)}
              </td>

              <td>
                ₹
                {data?.parts
                  ?.reduce((sum, p) => {
                    const repairCharges = Number(p.repairCharges) || 0;
                    return sum + repairCharges;
                  }, 0)
                  .toFixed(2)}
              </td>

              <td>
                ₹
                {data?.parts
                  ?.reduce((sum, p) => {
                    const paintingCharges = Number(p.paintingCharges) || 0;
                    return sum + paintingCharges;
                  }, 0)
                  .toFixed(2)}
              </td>
              <td colSpan="5"></td>
            </tr>
          )}
        </tbody>
      </table>

      <table className="labor-table">
        <thead>
          <tr>
            <th>LABOUR TYPE</th>
            <th>TAXABLE AMOUNT</th>
            <th>TAX AMOUNT</th>
            <th>TOTAL AMOUNT</th>
            <th>DEP AMOUNT</th>
          </tr>
        </thead>

        {/* <tbody>
          {data?.labourDetails?.length > 0 ? (
            data.labourDetails.map((labour, i) => (
              <tr key={i}>
                <td>{labour.labourType || '-'}</td>
                <td>
                  {labour.taxableAmount && labour.taxableAmount !== 'Not Found'
                    ? `₹${labour.taxableAmount}`
                    : '-'}
                </td>
                <td>
                  {labour.taxAmount && labour.taxAmount !== 'Not Found'
                    ? `₹${labour.taxAmount}`
                    : '-'}
                </td>
                <td>
                  {labour.totalAmount && labour.totalAmount !== 'Not Found'
                    ? `₹${labour.totalAmount}`
                    : '-'}
                </td>
                <td>
                  {labour.depAmount && labour.depAmount !== 'Not Found'
                    ? `₹${labour.depAmount}`
                    : '-'}
                </td>
              </tr>
            ))
          ) : (
            <>
              <tr>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
              </tr>
              <tr>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
              </tr>
            </>
          )}
        </tbody> */}
        {/* <tbody>
          {labourTypes.map((item, i) => {
            const taxable = getSubtotal(item.key);
            const tax = (taxable * 18) / 100;
            const total = taxable + tax;
            const dep = getDepAmount(item.key);

            return (
              <tr key={i}>
                <td>{item.label}</td>
                <td>₹{taxable.toFixed(2)}</td>
                <td>₹{tax.toFixed(2)}</td>
                <td>₹{total.toFixed(2)}</td>
                <td>₹{dep.toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody> */}
        <tbody>
          {labourTypes.map((item, i) => {
            const taxable = getSubtotal(item.key);
            const tax = (taxable * 18) / 100;
            // const total = taxable + tax;

            const apiLabour = getLabourFromAPI(item.label);

            const dep =
              apiLabour?.depAmount && apiLabour.depAmount !== "Not Found"
                ? Number(apiLabour.depAmount)
                : 0;
            const total = taxable + tax - dep;
            return (
              <tr key={i}>
                <td>{item.label}</td>
                <td>₹{taxable.toFixed(2)}</td>
                <td>₹{tax.toFixed(2)}</td>
                <td>₹{total.toFixed(2)}</td>
                <td>₹{dep.toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <table className="final-table">
        <thead>
          <tr>
            <th>FINAL CALCULATION</th>
            <th>AMOUNT</th>
          </tr>
        </thead>
        <tbody>
          {/* <tr>
            <td>Total Liability</td>
            <td>
              {data?.finalCalculation?.totalLiability &&
              data.finalCalculation.totalLiability !== "Not Found"
                ? `₹${data.finalCalculation.totalLiability}`
                : "--"}
            </td>
          </tr> */}
          <tr>
            <td>Total Liability</td>
            <td>₹{totalLiability.toFixed(2)}</td>
          </tr>
          <tr>
            <td>Compulsory Deductible</td>
            <td>
              {data?.finalCalculation?.compulsoryDeductible &&
              data.finalCalculation.compulsoryDeductible !== "Not Found"
                ? `₹${data.finalCalculation.compulsoryDeductible}`
                : "--"}
            </td>
          </tr>
          <tr>
            <td>Voluntary Deductible</td>
            <td>
              {data?.finalCalculation?.voluntaryDeductible &&
              data.finalCalculation.voluntaryDeductible !== "Not Found"
                ? `₹${data.finalCalculation.voluntaryDeductible}`
                : "--"}
            </td>
          </tr>
          <tr>
            <td>Salvage Deductions</td>
            <td>
              {data?.finalCalculation?.salvageDeductions &&
              data.finalCalculation.salvageDeductions !== "Not Found"
                ? `₹${data.finalCalculation.salvageDeductions}`
                : "--"}
            </td>
          </tr>
          <tr>
            <td>Non-Standard Amount</td>
            <td>
              {data?.finalCalculation?.nonStandardAmount &&
              data.finalCalculation.nonStandardAmount !== "Not Found"
                ? `₹${data.finalCalculation.nonStandardAmount}`
                : "--"}
            </td>
          </tr>
          <tr>
            <td>Towing Amount</td>
            <td>
              {data?.finalCalculation?.towingAmount &&
              data.finalCalculation.towingAmount !== "Not Found"
                ? `₹${data.finalCalculation.towingAmount}`
                : "--"}
            </td>
          </tr>
          <tr className="total-row">
            <td>Insurer Liability</td>
            {/* <td>
              {data?.finalCalculation?.digitLiability &&
              data.finalCalculation.digitLiability !== "Not Found"
                ? `₹${data.finalCalculation.digitLiability}`
                : "--"}
            </td> */}
            <td>₹{insurerLiability.toFixed(2)}</td>
          </tr>
        </tbody>
      </table>

      <div className="mobile-only">
        <h4 className="section-heading">PARTS</h4>

        {data?.parts?.map((part, i) => {
          const qty = Number(part.qty);
          const rate = Number(part.rate);
          const taxPercent = Number(part.taxPercent);
          const depPercent = Number(part.depPercent);

          const taxableAmount = qty * rate;
          const taxAmount = (taxableAmount * taxPercent) / 100;
          const totalAmount = taxableAmount + taxAmount;
          const depAmount = (taxableAmount * depPercent) / 100;

          return (
            <div key={i} className="report-box">
              <div className="row sn-row">
                <span className="label">SN</span>
                <span className="value">{i + 1}</span>
              </div>
              <div className="row">
                <span className="label">Job</span>
                <span className="value">{part.job}</span>
              </div>
              <div className="row">
                <span className="label">Material</span>
                <span className="value">{part.material}</span>
              </div>
              <div className="row">
                <span className="label">Description</span>
                <span className="value">{part.description}</span>
              </div>
              <div className="row">
                <span className="label">HSN</span>
                <span className="value">{part.hsn}</span>
              </div>
              <div className="row">
                <span className="label">Qty</span>
                <span className="value">{part.qty}</span>
              </div>
              <div className="row">
                <span className="label">Part Rate</span>
                <span className="value">₹{part.rate}</span>
              </div>
              <div className="row">
                <span className="label">Taxable Amount</span>
                <span className="value">₹{taxableAmount.toFixed(2)}</span>
              </div>
              <div className="row">
                <span className="label">Tax %</span>
                <span className="value">{part.taxPercent}</span>
              </div>
              <div className="row">
                <span className="label">Tax Amount</span>
                <span className="value">₹{taxAmount.toFixed(2)}</span>
              </div>
              <div className="row">
                <span className="label">Total Amount</span>
                <span className="value">₹{totalAmount.toFixed(2)}</span>
              </div>
              <div className="row">
                <span className="label">Dep %</span>
                <span className="value">{part.depPercent}</span>
              </div>
              <div className="row">
                <span className="label">Dep Amount</span>
                <span className="value">₹{depAmount.toFixed(2)}</span>
              </div>
              <div className="row">
                <span className="label">R&R Charges</span>
                <span className="value">₹{part.rrCharges}</span>
              </div>
              <div className="row">
                <span className="label">Repair Charges</span>
                <span className="value">₹{part.repairCharges}</span>
              </div>
              <div className="row">
                <span className="label">Painting Charges</span>
                <span className="value">₹{part.paintingCharges}</span>
              </div>
            </div>
          );
        })}

        <h4 className="section-heading">LABOUR CHARGES</h4>
        <div className="report-box">
          <div className="row">
            <span className="label">Labour Type</span>
            <span className="value">-</span>
          </div>
          <div className="row">
            <span className="label">Taxable Amount</span>
            <span className="value">-</span>
          </div>
          <div className="row">
            <span className="label">Tax Amount</span>
            <span className="value">-</span>
          </div>
          <div className="row">
            <span className="label">Total Amount</span>
            <span className="value">-</span>
          </div>
          <div className="row">
            <span className="label">Dep Amount</span>
            <span className="value">-</span>
          </div>
        </div>

        <h4 className="section-heading">SUBTOTAAL</h4>
        <div className="report-box subtotal-box">
          <div className="row total">
            <span className="label">Subtotal (Taxable)</span>
            <span className="value">
              ₹
              {data.parts
                .reduce((sum, p) => sum + Number(p.qty) * Number(p.rate), 0)
                .toFixed(2)}
            </span>
          </div>
          <div className="row total">
            <span className="label">Total Tax</span>
            <span className="value">
              ₹
              {data.parts
                .reduce((sum, p) => {
                  const taxBase = Number(p.qty) * Number(p.rate);
                  return sum + (taxBase * Number(p.taxPercent)) / 100;
                }, 0)
                .toFixed(2)}
            </span>
          </div>
          <div className="row total">
            <span className="label">Grand Total</span>
            <span className="value">
              ₹
              {data.parts
                .reduce((sum, p) => {
                  const taxBase = Number(p.qty) * Number(p.rate);
                  const taxAmount = (taxBase * Number(p.taxPercent)) / 100;
                  return sum + taxBase + taxAmount;
                }, 0)
                .toFixed(2)}
            </span>
          </div>
          <div className="row total">
            <span className="label">Total Depreciation</span>
            <span className="value">
              ₹
              {data.parts
                .reduce((sum, p) => {
                  const taxBase = Number(p.qty) * Number(p.rate);
                  return sum + (taxBase * Number(p.depPercent)) / 100;
                }, 0)
                .toFixed(2)}
            </span>
          </div>
          <div className="row total">
            <span className="label">Total R&R Charges</span>
            <span className="value">
              ₹
              {data.parts
                .reduce((sum, p) => {
                  const rrCharges = Number(p.rrCharges) || 0;
                  return sum + rrCharges;
                }, 0)
                .toFixed(2)}
            </span>
          </div>
          <div className="row total">
            <span className="label">Total Repair Charges</span>
            <span className="value">
              ₹
              {data.parts
                .reduce((sum, p) => {
                  const repairCharges = Number(p.repairCharges) || 0;
                  return sum + repairCharges;
                }, 0)
                .toFixed(2)}
            </span>
          </div>
          <div className="row total">
            <span className="label">Total Painting Charges</span>
            <span className="value">
              ₹
              {data.parts
                .reduce((sum, p) => {
                  const paintingCharges = Number(p.paintingCharges) || 0;
                  return sum + paintingCharges;
                }, 0)
                .toFixed(2)}
            </span>
          </div>
        </div>

        <h4 className="section-heading">FINAL CALCULATION</h4>
        <div className="report-box">
          <div className="row">
            <span className="label">Total Liability</span>
            <span className="value">--</span>
          </div>
          <div className="row">
            <span className="label">Compulsory Deductible</span>
            <span className="value">--</span>
          </div>
          <div className="row">
            <span className="label">Voluntary Deductible</span>
            <span className="value">--</span>
          </div>
          <div className="row">
            <span className="label">Salvage Deductions</span>
            <span className="value">--</span>
          </div>
          <div className="row">
            <span className="label">Non-Standard Amount</span>
            <span className="value">--</span>
          </div>
          <div className="row">
            <span className="label">Towing Amount</span>
            <span className="value">--</span>
          </div>
          <div className="row total">
            <span className="label">Insurer Liability</span>
            <span className="value">--</span>
          </div>
        </div>
      </div>

      <div className="note-section">
        <div className="section-heading">ASSESSMENT NOTES</div>
        <ul className="assessment-notes">
          <li>
            Any additional amount over and above the approved assessment shall
            be borne by the Insured.
          </li>
          <li>
            The final liability has been arrived at as full and final settlement
            after consent from the Insured. Do get back to us within the next 24
            hours for any queries or clarifications.
          </li>
        </ul>
      </div>
    </div>
  );
};

export default InitialAssessmentReportManual;
